import classNames from 'clsx';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import CasinoGameHeader, { validByCheck } from '@swf/components/CasinoGameHeader/CasinoGameHeader';
import CasinoGameIcon from '@swf/components/CasinoGameIcon/CasinoGameIcon';
import CasinoGameList from '@swf/components/CasinoGameList/CasinoGameList';
import NoGamesFound from '@swf/components/NoGamesFound/NoGamesFound';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import useChangeCurrency from '@swf/core/hooks/useChangeCurrency';
import usePrevious from '@swf/core/hooks/usePrevious';
import favorites from '@swf/core/reducers/favorites';
import freeSpins from '@swf/core/reducers/freeSpins';
import gameTracking from '@swf/core/reducers/gameTracking';
import { select } from '@swf/core/store';
import type { StreamData } from '@swf/core/types/CasinoGame';
import type { WidgetFC, WidgetInstanceProps } from '@swf/core/types/Widget';
import { asPage, buildPayload } from '@swf/core/utils/casino';
import { Service } from '@swf/core/utils/casinoFilters';
import type { CasinoIcon } from '@swf/generated/types/common/Casino';
import type { CasinoServiceParameters } from '@swf/generated/types/common/CasinoParams';
import type {
  CasinoGameSliderWidgetConfig,
  CasinoGameSliderWidgetServices
} from '@swf/generated/types/widgets/CasinoGameSliderWidget';

import styles from './CasinoGameSliderWidget.scss';

export interface Data {
  games: StreamData;
}

export type Props = WidgetInstanceProps<
  CasinoGameSliderWidgetConfig,
  CasinoGameSliderWidgetServices,
  Data
>;

const CasinoGameSliderWidget: WidgetFC<Props> = ({
  config,
  data = {} as Data,
  fetchService,
  preload,
  services,
  style
}) => {
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const { title, filters, fsbPlaceholder, minGames } = config;
  const [activeFilter, setActiveFilter] = useState<number>(0);
  const oldFilter = usePrevious(activeFilter);
  const hasHeader = title || filters !== 'none';
  const { name: serviceName, onAuth } = services.games.config;
  const fsbWidget = !!serviceName?.match(Service.FreeSpinBonus);

  const icon = get(services, 'games.params.ims.tags[0]', 'icnSquare') as CasinoIcon;
  const games = data?.games?.games || [];
  const loading = !data?.games?.games;
  const noGamesFound = isEmpty(get(data, 'games.games', [])) && !loading;

  const sliderRef = React.createRef<SliderHandles>();
  const containerRef = React.createRef<HTMLDivElement>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });

  const siteName = useSelector(select.site.getName);

  const shouldRender = games?.length >= minGames && ((onAuth && isAuthenticated) || !onAuth);

  const pageGames = useMemo(() => [asPage(data.games)], [data.games]);
  const showVegasContent = useSelector(select.site.showVegasContent);

  useChangeCurrency(() => fetchService('games'));

  useEffect(() => {
    if (activeFilter !== oldFilter) {
      const { meta, params } = buildPayload({
        activeFilter,
        showVegasContent,
        siteName,
        defaultParams: get(services, 'games.params', {}) as CasinoServiceParameters,
        filters: config.filters
      });
      fetchService('games', params, meta);
    }
  }, [oldFilter, activeFilter, fetchService, config.filters, showVegasContent, siteName, services]);

  return shouldRender ? (
    <section
      className={classNames(styles.container, styles[`${icon}Container`])}
      style={style}
      ref={containerRef}
    >
      <Vary by={[Header.Country, Header.Currency]} />
      {hasHeader && (
        <CasinoGameHeader
          {...config}
          slide
          data={pageGames}
          icon={icon}
          sliderRef={sliderRef}
          sliderState={sliderState}
          onFilter={setActiveFilter}
          active={activeFilter}
          widgetRef={containerRef}
          isValid={validByCheck}
        />
      )}
      {noGamesFound ? (
        <NoGamesFound />
      ) : (
        <CasinoGameList
          {...config}
          preload={preload}
          games={games}
          icon={icon}
          slide
          loading={loading}
          onScroll={setSliderState}
          placeholder={fsbPlaceholder ? <CasinoGameIcon FSBPlaceholder /> : undefined}
          sliderRef={sliderRef}
          fsbLaunch={fsbWidget}
        />
      )}
    </section>
  ) : null;
};

CasinoGameSliderWidget.serviceReducers = { favorites, freeSpins };
CasinoGameSliderWidget.trackingReducer = gameTracking;

export default CasinoGameSliderWidget;
